import React, { useEffect, useRef, useState } from 'react';

const ManagementSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animStep, setAnimStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Phone animation - 6 seconds per step
  useEffect(() => {
    if (!isVisible) return;
    
    const STEP_DURATION = 6000;
    
    const interval = setInterval(() => {
      setAnimStep(prev => (prev + 1) % 6);
    }, STEP_DURATION);
    
    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section 
      className="management-section" 
      ref={sectionRef}
      data-testid="management-section"
    >
      <div className="management-content">
        <div className="management-layout">
          {/* Left: Phone Mockup */}
          <div className="management-left">
            <div className={`phone-mockup-wrapper ${isVisible ? 'visible' : ''}`}>
              <div className="phone-frame-new">
                <div className="phone-notch-new"></div>
                <div className="phone-screen-new">
                  {/* Dashboard Header */}
                  <div className="dash-header-new">
                    <span className="dash-title-new">AI Subscriptions</span>
                    <span className="dash-count-new">{animStep >= 3 ? '3 Active' : '4 Active'}</span>
                  </div>

                  {/* Cancel Modal */}
                  {animStep === 2 && (
                    <div className="cancel-modal-new">
                      <div className="cancel-modal-content-new">
                        <div className="cancel-icon-new">⚠️</div>
                        <span className="cancel-title-new">Cancel Subscription?</span>
                        <span className="cancel-desc-new">Midjourney - $10/mo</span>
                        <div className="cancel-buttons-new">
                          <button className="cancel-btn-no-new">Keep</button>
                          <button className="cancel-btn-yes-new">Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Credits Modal */}
                  {animStep === 4 && (
                    <div className="credits-modal-new">
                      <div className="credits-modal-content-new">
                        <div className="credits-header-new">Add Credits</div>
                        <div className="credits-options-new">
                          <div className="credit-option-new">$10 → 100</div>
                          <div className="credit-option-new selected">$25 → 300</div>
                          <div className="credit-option-new">$50 → 700</div>
                        </div>
                        <button className="add-credits-btn-new">Add $25</button>
                      </div>
                    </div>
                  )}

                  {/* Credits Success */}
                  {animStep === 5 && (
                    <div className="credits-success-new">
                      <div className="success-icon-new">✓</div>
                      <span className="success-text-new">Credits Added!</span>
                      <span className="success-amount-new">+300 credits</span>
                    </div>
                  )}

                  {/* Subscriptions */}
                  {(animStep === 0 || animStep === 1 || animStep === 3) && (
                    <div className="dash-subs-new">
                      <div className="sub-card-new active-sub-new">
                        <div className="sub-icon-new gpt">GPT</div>
                        <div className="sub-info-new">
                          <span className="sub-name-new">ChatGPT Plus</span>
                          <span className="sub-status-new">Active</span>
                        </div>
                        <span className="sub-price-new">$20/mo</span>
                      </div>
                      <div className="sub-card-new active-sub-new">
                        <div className="sub-icon-new claude">C</div>
                        <div className="sub-info-new">
                          <span className="sub-name-new">Claude Pro</span>
                          <span className="sub-status-new">Active</span>
                        </div>
                        <span className="sub-price-new">$20/mo</span>
                      </div>
                      <div className={`sub-card-new ${animStep === 1 ? 'selected-sub-new' : ''} ${animStep === 3 ? 'cancelled-sub-new' : ''}`}>
                        <div className="sub-icon-new midj">M</div>
                        <div className="sub-info-new">
                          <span className="sub-name-new">Midjourney</span>
                          <span className={`sub-status-new ${animStep === 3 ? 'cancelled' : ''}`}>
                            {animStep === 3 ? 'Cancelled' : 'Active'}
                          </span>
                        </div>
                        <span className="sub-price-new">{animStep === 3 ? '-' : '$10/mo'}</span>
                      </div>
                    </div>
                  )}

                  {/* Usage */}
                  {(animStep === 0 || animStep === 1 || animStep === 3) && (
                    <div className="dash-usage-new">
                      <span className="usage-title-new">Monthly Usage</span>
                      <div className="usage-bars-new">
                        <div className="usage-bar-new"><div className="bar-fill-new" style={{width: '80%'}}></div><span>GPT</span></div>
                        <div className="usage-bar-new"><div className="bar-fill-new" style={{width: '45%'}}></div><span>Claude</span></div>
                        {animStep !== 3 && (
                          <div className="usage-bar-new"><div className="bar-fill-new" style={{width: '60%'}}></div><span>Midj</span></div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  {(animStep === 0 || animStep === 1 || animStep === 3) && (
                    <div className="dash-total-new">
                      <span>Total Monthly</span>
                      <span className="total-amount-new">{animStep === 3 ? '$40.00' : '$50.00'}</span>
                    </div>
                  )}

                  {/* Nav */}
                  <div className="app-nav-new">
                    <div className="nav-item-new">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      </svg>
                    </div>
                    <div className="nav-item-new active">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 20V10M12 20V4M6 20v-6"/>
                      </svg>
                    </div>
                    <div className="nav-item-new">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="management-right">
            <div className={`section-header-inline ${isVisible ? 'visible' : ''}`}>
              <span className="section-number-small">04</span>
              <p className="section-label">Management</p>
              <h2 className="section-title">One Dashboard, All AI</h2>
              <p className="section-description">
                Manage all your AI subscriptions from a single interface. Track usage, control spending, 
                and switch between services seamlessly, all without exposing your identity.
              </p>
            </div>

            <div className={`management-cards ${isVisible ? 'visible' : ''}`}>
              <div className="management-card" style={{ animationDelay: '0.1s' }}>
                <div className="card-number">01</div>
                <h4>Unified Dashboard</h4>
                <p>View all AI services in one place</p>
              </div>
              <div className="management-card" style={{ animationDelay: '0.2s' }}>
                <div className="card-number">02</div>
                <h4>Purchase & Top Up</h4>
                <p>Add credits instantly with crypto</p>
              </div>
              <div className="management-card" style={{ animationDelay: '0.3s' }}>
                <div className="card-number">03</div>
                <h4>Usage Analytics</h4>
                <p>Track spending across all services</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManagementSection;
